import queryString from 'query-string';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import isEmpty from 'lodash/isEmpty';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import get from 'lodash/get';

import nwApiSDK from './nw-api-sdk';

// Local Dependencies
const { ServiceSDK } = nwApiSDK;

// Use null object pattern so we don't have to check for cache
export const nullObjCache = {
  get(_, callback) {
    callback();
  },
  set() {},
  del() {},
};

// The URL used to cache data may include parts of the page URL input by a user. It's possible
// that a user can input a malformed or malicious URL that we're still able to serve data from.
// In that case, we should not add the malformed URL to the cache.
// We only allow alphanumberic characters and -_/{}[\]"".,:?= which are the characters we need
// for URLs and JSON bodies; any cache key that contains a character not in that list is invalid
// TODO: Remove once WAF covers www.nerdwallet.com
const isValidCacheKey = (cacheKey) =>
  cacheKey.search(/[^\w\d\-_/{}[\]"".,:?=]/g) === -1;

export const nwFetch = ({
  headers,
  timeout = 10000,
  url,
  method = 'GET',
  params,
  json,
  cache = nullObjCache,
  rawCacheClient = nullObjCache,
  cacheExpiration = 600, // 10 minutes
  bypassCache = false,
  bypassCacheWrite = false,
  cookies,
  logger,
  serverRequest,
}) =>
  new Promise((resolve, reject) => {
    // Build cacheKey
    const queryParams = isEmpty(params)
      ? ''
      : `?${queryString.stringify(params)}`;
    const compositeUrl = `${url}${queryParams}`;
    const cacheKey = json
      ? `${compositeUrl}${JSON.stringify(json)}`
      : compositeUrl;

    const validCacheKey = isValidCacheKey(cacheKey);

    cache.get(cacheKey, (cacheErr, value) => {
      if (value && !bypassCache) {
        // Cache hit!
        resolve(JSON.parse(value));
      } else {
        // Cache miss / expired / bypassed
        ServiceSDK.http({
          url,
          method,
          headers,
          params,
          json,
          timeout,
          cookies,
          serverRequest,
          callback: (err, resp, body) => {
            const statusCode = get(resp, 'statusCode');
            if (!err && resp && body && statusCode === 200) {
              if (!bypassCacheWrite && validCacheKey) {
                cache.set(
                  cacheKey,
                  JSON.stringify(body),
                  { EX: cacheExpiration },
                  (error) => {
                    if (logger && logger.error && error) {
                      logger.error(
                        error,
                        `[REDIS] - FAIL: could not set ${cacheKey} on cache`
                      );
                    }
                  }
                );
              } else if (logger) {
                logger.info(
                  `Bypassing cache write for ${cacheKey} because ${
                    bypassCacheWrite
                      ? 'bypass was requested'
                      : 'cache key is invalid'
                  }`
                );
              }

              resolve(body);
            } else if (statusCode === 404) {
              // we get 404s whenever we try to fetch landing pages from WP that do not exist
              if (bypassCache) {
                // since we're bypassing the cache, this resource might be cached, even though it no longer exists
                // remove it and log the removal
                if (logger) {
                  logger.info(
                    `[REDIS] Got a 404 trying to retrieve ${cacheKey}. Attempting to remove from cache.`
                  );
                }
                rawCacheClient.del(cacheKey);
              }
              if (logger) {
                logger.warn(
                  `ServiceSDK.http ${method} ${statusCode} ${compositeUrl}`
                );
              }
              resolve();
            } else {
              reject(
                err ||
                  new Error(
                    `ServiceSDK.http ${method} ${statusCode} ${compositeUrl}`
                  )
              );
            }
          },
        });
      }
    });
  });

export default { nwFetch };
